// Grading an error payload into a `TaskFailureKind`: the wire-code map derived
// from the producer table, and the prose marker a withdrawn model serves in
// place of a code. See `../task.ts` for the file docblock.

import { ErrorPayloadSchema } from '../../value-objects/task-run-event';
import { TaskFailureKind, TaskFailureKindSchema } from './failure-kind';
import { TASK_FAILURE_PRODUCER, TaskFailureSeam } from './failure-producer';

/**
 * The phrasings a provider has served IN PLACE OF an error code when a model was
 * withdrawn — the vocabulary {@link TaskFailureKind.ProviderProseAnswer} is graded
 * from. A sibling of `LLM_EXHAUSTION_MARKERS` / `LLM_CONTEXT_OVERFLOW_MARKERS`
 * (`llm-credential.ts`) kept beside the kind it names for the same reason those two
 * are kept together: prose enters this platform at ONE point, so the next vendor's
 * wording is one line here rather than a second matcher on whichever lane met it.
 *
 * ONE MARKER CARRIES LIVE PROD ROWS AND THAT IS THE WHOLE LIST, measured 2026-09-02
 * over `task_run_events` where `kind='error'`, 7 days: `Stealth Ox Alpha` — **555
 * rows**, one phrasing, byte-identical (`GROUP BY left(payload::text, 200)` returned
 * a single group). A second phrasing was looked for and not found; none is invented.
 * `This model has been deprecated` / `is no longer available` are real wordings a
 * gateway might serve and are NOT here, because they are unmeasured on this platform
 * — the same "deferred, not missed" posture the overflow set states, and for the same
 * asymmetry: a marker that does not fire leaves the row in `runner_error` and loses a
 * label, a marker that fires wrongly mislabels a box fault as a provider one.
 *
 * Anchored on the PRODUCT NAME rather than on "Thank you for participating", which
 * is ordinary English a model could quote back from a customer's repo.
 */
const PROVIDER_PROSE_ANSWER_MARKERS = /Stealth Ox Alpha/i;

/**
 * Does this error text carry a withdrawn-model farewell in place of an error code?
 * The exported half of {@link PROVIDER_PROSE_ANSWER_MARKERS}, shaped exactly like
 * `hasLlmExhaustionMarker`: it answers only the BODY question, and the CALLER owns
 * which statuses may ask it (`apps/runner`'s `providerFailureCode` asks it LAST — on
 * a status-less planner error's own text and on a 4xx body no other arm claimed —
 * never on a 5xx, which is the endpoint's own fault whatever it says, and never on a
 * 2xx, where the phrase is a model quoting something back at us).
 *
 * Pure and total over any string; `''` is `false`. No `g` flag, so repeated calls
 * cannot disagree through `lastIndex`.
 */
export function hasProviderProseAnswerMarker(body: string): boolean {
  return PROVIDER_PROSE_ANSWER_MARKERS.test(body);
}

/**
 * Wire `code` → {@link TaskFailureKind}. DERIVED from
 * {@link TASK_FAILURE_PRODUCER}: exactly the kinds declared `wire`, and the code
 * IS the kind's own value.
 *
 * `ErrorPayloadSchema.code` is an optional machine-readable code the box may
 * stamp on a terminal `error` event. A code that is absent, or present and not
 * in this map, classifies as {@link TaskFailureKind.RunnerError} — exactly
 * today's behaviour, so a producer that says nothing loses nothing.
 *
 * It used to be a hand-written map beside the enum. Two drifts were possible and
 * one of them happened: a kind with a code and no writer (`git_auth`, a whole
 * slice at zero rows), and a code that no kind claims. Derivation removes both —
 * membership is the seam declaration, and the identity mapping is what makes the
 * `wire` reachability check in `task.test.ts` a grep the compiler agrees with.
 * Adding a code is still a deliberate decision; it is now the same decision as
 * naming the producer.
 */
export const TASK_FAILURE_WIRE_CODE: Readonly<Record<string, TaskFailureKind>> = Object.freeze(
  Object.fromEntries(
    TaskFailureKindSchema.options.flatMap((kind): (readonly [string, TaskFailureKind])[] => {
      const producer = TASK_FAILURE_PRODUCER[kind];
      switch (producer.seam) {
        case TaskFailureSeam.Wire:
        case TaskFailureSeam.WireUnproduced:
          return [[kind, kind] as const];
        case TaskFailureSeam.Platform:
          return [];
        default: {
          // Totality has two halves and this is the second. Over KINDS it is the
          // `Record<TaskFailureKind, TaskFailureProducer>` above: a kind with no
          // producer declaration does not compile. Over SEAMS it is here: a new
          // `TaskFailureSeam` arm does not compile until this switch decides
          // whether it reaches the wire, and throws at import if one is ever
          // introduced at runtime. Neither half can be satisfied by a default.
          const unreachable: never = producer;
          throw new Error(`unclassified failure producer seam: ${JSON.stringify(unreachable)}`);
        }
      }
    }),
  ),
);

/**
 * Classify a terminal `error` run event's payload into a {@link TaskFailureKind}.
 * Pure and total. A payload the wire `ErrorPayloadSchema` rejects — null,
 * missing `message`, a drifted shape — is `error_unclassified` (budgeted to a
 * single attempt, see {@link TASK_FAILURE_BUDGET}). A payload it accepts is
 * graded by its optional machine `code` through {@link TASK_FAILURE_WIRE_CODE},
 * and falls to `runner_error` when it carries none or carries one this platform
 * has not budgeted — exactly the pre-slice-11 behaviour, so a producer that
 * stamps nothing loses nothing.
 *
 * There is still deliberately NO message-text heuristic. Grading free text would
 * be a guess that decides real spend, and the whole reason the provider arms
 * exist is that guessing put an account condition on a task's ladder. The
 * vocabulary grows in `TASK_FAILURE_WIRE_CODE`, with an explicit budget per
 * code.
 */
export function classifyTaskFailure(payload: unknown): TaskFailureKind {
  const parsed = ErrorPayloadSchema.safeParse(payload);
  if (!parsed.success) return TaskFailureKind.ErrorUnclassified;
  const code = parsed.data.code;
  // The code is an ENUMERATION lookup, never a prefix/substring test: a producer
  // that stamps `provider_rate_limit_soft` has not agreed to this budget, and a
  // match-by-shape would silently enrol it.
  //
  // `Object.hasOwn`, not a bare index. The code is attacker-adjacent — it arrives on a
  // run event from a box — and a plain lookup resolves through the prototype chain, so
  // `code: "toString"` returned the FUNCTION `Object.prototype.toString` as a
  // `TaskFailureKind`. That value then reaches `TASK_FAILURE_BUDGET[kind]`, which has no
  // entry for it, and is written to `tasks.failure_kind`. Verified before the fix:
  // `toString`, `constructor`, `valueOf` and `hasOwnProperty` all resolved; every other
  // unknown string correctly fell through to `runner_error`.
  if (code === undefined || !Object.hasOwn(TASK_FAILURE_WIRE_CODE, code)) {
    return TaskFailureKind.RunnerError;
  }
  return TASK_FAILURE_WIRE_CODE[code] ?? TaskFailureKind.RunnerError;
}
